import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUpdateUserSign from '@/hooks/users/use-update-user-sign';
import useUserInfo from '@/hooks/users/use-user-info';

export default function MySignatureRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: updateSignature, isPending } = useUpdateUserSign();

  const {
    handleSubmit,
    resetField,
    formState: { isDirty },
    control,
  } = useForm<UpdateSignatureFormData>({
    resolver: zodResolver(updateSignatureFormScheme),
    defaultValues: { signature: '' },
  });

  useAdjustLayout();

  const handleUpdateSignature = async (data: UpdateSignatureFormData) => {
    await updateSignature(data.signature);

    resetField('signature', { defaultValue: data.signature });

    navigate(-1);
  };

  useEffect(() => {
    if (userInfo?.Sign) {
      resetField('signature', {
        defaultValue: userInfo.Sign,
      });
    }
  }, [resetField, userInfo?.Sign]);

  return (
    <PageLayout
      title={t('personal-info.edit-signature')}
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        className='mx-5 mt-4 mb-safe-offset-4 flex flex-1 flex-col justify-between'
        onSubmit={handleSubmit(handleUpdateSignature)}>
        <label
          htmlFor='signature'
          className='form-control w-full'>
          <FormInput
            showClearButton
            defaultValue=''
            control={control}
            helperText={t('fields.messages.max-length', { name: t('personal-info.signature'), count: 50 })}
            name='signature'
            maxLength={50}
          />
        </label>

        <ProgressButton
          as='submit'
          isLoading={isPending}
          disabled={!isDirty}
          className='btn btn-primary'
          label='common.confirm'
        />
      </form>
    </PageLayout>
  );
}

const updateSignatureFormScheme = z.object({
  signature: z.string().trim().min(0).max(50, 'errors.string.max-length|count=50'),
});

type UpdateSignatureFormData = z.infer<typeof updateSignatureFormScheme>;
